import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Modal,
  Label,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from '@byscott-io/corebyscott'
import { withUserStore } from '../../../contexts/UserStore'
import Tooltip from '../../Shared/Tooltip'
import Lex from '../../Shared/Lex'
import { useWaldoEvent, usePresenceContext } from '@byscott-io/where-is-waldo'
import useViewRecords from '../../../hooks/useViewRecords'
import useActivityVisibility from '../../../hooks/useActivityVisibility'
import CommentThread from '../CommentThread/CommentThread'
import TimeAgo from '../../Shared/TimeAgo'
import TagSelector from '../../Shared/TagSelector'

const UserSelectorModal = ({ isOpen, toggle, users = [], teams = [], currentUser, selectedUsers = new Set(), setSelectedUsers, selectedTeams = new Set(), setSelectedTeams, accountType }) => {
  const [tempSelectedUsers, setTempSelectedUsers] = useState(new Set(selectedUsers))
  const [tempSelectedTeams, setTempSelectedTeams] = useState(new Set(selectedTeams || []))
  const [activeTab, setActiveTab] = useState('users')

  useEffect(() => {
    if (isOpen) {
      setTempSelectedUsers(new Set(selectedUsers))
      setTempSelectedTeams(new Set(selectedTeams || []))
    }
  }, [selectedUsers, selectedTeams, isOpen])

  const handleUserToggle = (userId) => {
    const newSelected = new Set(tempSelectedUsers)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
    } else {
      newSelected.add(userId)
    }
    setTempSelectedUsers(newSelected)
  }

  const handleTeamToggle = (teamId) => {
    const newSelected = new Set(tempSelectedTeams)
    if (newSelected.has(teamId)) {
      newSelected.delete(teamId)
    } else {
      newSelected.add(teamId)
    }
    setTempSelectedTeams(newSelected)
  }

  const handleSelectAllUsers = () => {
    const validUsers = users.filter(u => u && u.id)
    const allUserIds = new Set([...(currentUser ? [currentUser.id] : []), ...validUsers.map(u => u.id)])
    setTempSelectedUsers(allUserIds)
  }

  const handleDeselectAllUsers = () => {
    setTempSelectedUsers(new Set())
  }

  const handleSelectAllTeams = () => {
    const allTeamIds = new Set(teams?.map(t => t.id) || [])
    setTempSelectedTeams(allTeamIds)
  }

  const handleDeselectAllTeams = () => {
    setTempSelectedTeams(new Set())
  }

  const handleApply = () => {
    setSelectedUsers(tempSelectedUsers)
    if (setSelectedTeams) {
      setSelectedTeams(tempSelectedTeams)
    }
    toggle()
  }

  const handleCancel = () => {
    setTempSelectedUsers(new Set(selectedUsers))
    setTempSelectedTeams(new Set(selectedTeams || []))
    toggle()
  }

  const isBusinessAccount = accountType === 'business'

  // Don't render if currentUser is not available
  if (!currentUser) {
    return (
      <div className="cbs-scope">
        <Modal open={isOpen} onClose={handleCancel} title="Loading..." size="sm">
          <div className="text-center p-3">
            <i className="fas fa-spinner fa-spin me-2"></i>
            Loading user data...
          </div>
        </Modal>
      </div>
    )
  }

  return (
    <div className="cbs-scope">
      <Modal
        open={isOpen}
        onClose={handleCancel}
        size={isBusinessAccount ? 'md' : 'sm'}
        title={<><i className="fas fa-users me-2"></i>Filter Activity by {isBusinessAccount ? 'Users & Teams' : 'Users'}</>}
        footer={
          <>
            <Button color="primary" size="sm" onClick={handleApply}>
              <i className="fas fa-filter me-1"></i>
              Apply Filter
            </Button>
            <Button color="secondary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        }
      >
        {isBusinessAccount ? (
          <>
            {/* Tabs for Business accounts */}
            <div className="mb-3 flex gap-2 border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                className={`px-3 py-2 text-sm ${activeTab === 'users' ? 'border-b-2 border-indigo-600 font-medium text-indigo-600' : 'text-gray-500'}`}
                onClick={() => setActiveTab('users')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-users me-1"></i>
                Users ({tempSelectedUsers.size})
              </button>
              <button
                type="button"
                className={`px-3 py-2 text-sm ${activeTab === 'teams' ? 'border-b-2 border-indigo-600 font-medium text-indigo-600' : 'text-gray-500'}`}
                onClick={() => setActiveTab('teams')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-user-friends me-1"></i>
                Teams ({tempSelectedTeams.size})
              </button>
            </div>

            {activeTab === 'users' && (
              <div>
                <div className="mb-3 d-flex gap-2">
                  <Button 
                    color="outline-primary" 
                    onClick={handleSelectAllUsers}
                    className="flex-fill btn-sm"
                  >
                    <i className="fas fa-check-double me-1"></i>
                    Select All Users
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    onClick={handleDeselectAllUsers}
                    className="flex-fill btn-sm"
                  >
                    <i className="fas fa-times me-1"></i>
                    Deselect All
                  </Button>
                </div>

                <div>
                  {/* Current User - Always at top */}
                  {currentUser && (
                    <div className="border border-gray-300 rounded-md p-2 mb-2 bg-light">
                      <Label className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={tempSelectedUsers.has(currentUser.id)}
                          onChange={() => handleUserToggle(currentUser.id)}
                          className="me-2"
                        />
                        <div className="flex-grow-1">
                          <div className="fw-semibold text-primary">
                            <i className="fas fa-user-circle me-1"></i>
                            Me ({String(currentUser.firstName || '')} {String(currentUser.lastName || '')})
                          </div>
                          <small className="text-muted">{String(currentUser.email || '')}</small>
                        </div>
                      </Label>
                    </div>
                  )}

                  {/* Other Users */}
                  {users.filter(u => u && (!currentUser || u.id !== currentUser.id)).map(user => (
                    <div key={user.id} className="border border-gray-300 rounded-md p-2 mb-1">
                      <Label className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={tempSelectedUsers.has(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="me-2"
                        />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">
                            {String(user.firstName || '')} {String(user.lastName || '')}
                          </div>
                          <small className="text-muted">{String(user.email || '')}</small>
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-center">
                  <small className="text-muted">
                    {tempSelectedUsers.size} of {users.filter(u => u && u.id).length + (currentUser ? 1 : 0)} users selected
                  </small>
                </div>
              </div>
            )}

            {activeTab === 'teams' && (
              <div>
                <div className="mb-3 d-flex gap-2">
                  <Button 
                    color="outline-primary" 
                    onClick={handleSelectAllTeams}
                    className="flex-fill btn-sm"
                  >
                    <i className="fas fa-check-double me-1"></i>
                    Select All Teams
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    onClick={handleDeselectAllTeams}
                    className="flex-fill btn-sm"
                  >
                    <i className="fas fa-times me-1"></i>
                    Deselect All
                  </Button>
                </div>

                <div>
                  {teams?.length > 0 ? teams.map(team => (
                    <div key={team.id} className="border border-gray-300 rounded-md p-2 mb-1">
                      <Label className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={tempSelectedTeams.has(team.id)}
                          onChange={() => handleTeamToggle(team.id)}
                          className="me-2"
                        />
                        <div className="flex-grow-1">
                          <div className="fw-semibold">
                            <i className="fas fa-users me-1"></i>
                            {team.name}
                          </div>
                          <small className="text-muted">{team.description || 'No description'}</small>
                        </div>
                      </Label>
                    </div>
                  )) : (
                    <div className="text-center text-muted p-3">
                      <i className="fas fa-user-friends fa-2x mb-2 d-block"></i>
                      No teams available
                    </div>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <small className="text-muted">
                    {tempSelectedTeams.size} of {teams?.length || 0} teams selected
                  </small>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Team account - original single-tab layout */
          <>
            <div className="mb-3 d-flex gap-2">
              <Button 
                color="outline-primary" 
                onClick={handleSelectAllUsers}
                className="flex-fill btn-sm"
              >
                <i className="fas fa-check-double me-1"></i>
                Select All
              </Button>
              <Button 
                color="outline-secondary" 
                onClick={handleDeselectAllUsers}
                className="flex-fill btn-sm"
              >
                <i className="fas fa-times me-1"></i>
                Deselect All
              </Button>
            </div>

            <div>
              {/* Current User - Always at top */}
              {currentUser && (
                <div className="border border-gray-300 rounded-md p-2 mb-2 bg-light">
                  <Label className="d-flex align-items-center">
                    <Input
                      type="checkbox"
                      checked={tempSelectedUsers.has(currentUser.id)}
                      onChange={() => handleUserToggle(currentUser.id)}
                      className="me-2"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-primary">
                        <i className="fas fa-user-circle me-1"></i>
                        Me ({String(currentUser.firstName || '')} {String(currentUser.lastName || '')})
                      </div>
                      <small className="text-muted">{String(currentUser.email || '')}</small>
                    </div>
                  </Label>
                </div>
              )}

              {/* Other Team Members */}
              {users.filter(u => u && (!currentUser || u.id !== currentUser.id)).map(user => (
                <div key={user.id} className="border border-gray-300 rounded-md p-2 mb-1">
                  <Label className="d-flex align-items-center">
                    <Input
                      type="checkbox"
                      checked={tempSelectedUsers.has(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      className="me-2"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold">
                        {String(user.firstName || '')} {String(user.lastName || '')}
                      </div>
                      <small className="text-muted">{String(user.email || '')}</small>
                    </div>
                  </Label>
                </div>
              ))}
            </div>

            <div className="mt-3 text-center">
              <small className="text-muted">
                {tempSelectedUsers.size} of {users.filter(u => u && u.id).length + (currentUser ? 1 : 0)} users selected
              </small>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

const ActivityItem = React.memo(({ activity, currentUser, isNew = false, isUnseen = false, onVisibilityRef, onMarkAsSeen }) => {
  const [commentButton, setCommentButton] = useState(null)

  const getActivityIcon = () => {
    switch (activity.type) {
      case 'journal_entry':
        return <i className="fas fa-journal-whills text-primary"></i>
      case 'task_completed':
        return <i className="fas fa-check-circle text-success"></i>
      case 'task_created':
        return <i className="fas fa-plus-circle text-info"></i>
      case 'goal_created':
        return <i className="fas fa-bullseye text-warning"></i>
      case 'strategy_created':
        return <i className="fas fa-chess text-purple"></i>
      default:
        return <i className="fas fa-activity text-secondary"></i>
    }
  }

  const getUserName = (user) => {
    if (!user || !currentUser) return 'Unknown User'
    if (user.id === currentUser.id) return 'You'
    return `${String(user.firstName || '')} ${String(user.lastName || '')}`.trim() || 'Unknown User'
  }

  const getActivityDescription = () => {
    switch (activity.type) {
      case 'journal_entry':
        // Use CommentThread to enable comments on journal entries
        if (activity.journalEntry) {
          const journalContent = (
            <div>
              <div className="mb-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{getUserName(activity.user)}</strong> posted <TimeAgo date={new Date(activity.journalEntry.createdAt)} />
                  {activity.goal && (
                    <Badge color="primary" className="ms-2">
                      <i className="fas fa-bullseye me-1"></i>
                      {activity.goal.name}
                    </Badge>
                  )}
                  {activity.task && (
                    <Badge color="secondary" className="ms-1">
                      <i className="fas fa-tasks me-1"></i>
                      {activity.task.name}
                    </Badge>
                  )}
                </div>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {commentButton}
                  <TagSelector taggable={{...activity.journalEntry, recordType: 'JournalEntry'}} />
                </span>
              </div>
              <div className="mt-1 text-muted">
                &ldquo;{activity.content}&rdquo;
              </div>
            </div>
          )
          return (
            <CommentThread
              depth={0}
              thread={null}
              content={journalContent}
              commentable={activity.journalEntry}
              editing={false}
              isReply={false}
              onGetCommentButton={setCommentButton}
            />
          )
        }
        // Fallback to simple display if no journalEntry data
        return (
          <div>
            <strong>{getUserName(activity.user)}</strong> logged progress
            {activity.goal && (
              <Badge color="primary"  className="ms-2">
                <i className="fas fa-bullseye me-1"></i>
                {activity.goal.name}
              </Badge>
            )}
            {activity.task && (
              <Badge color="secondary"  className="ms-1">
                <i className="fas fa-tasks me-1"></i>
                {activity.task.name}
              </Badge>
            )}
            <div className="mt-1 text-muted small">
              &ldquo;{activity.content}&rdquo;
            </div>
          </div>
        )
      case 'task_completed':
        return (
          <div>
            <strong>{getUserName(activity.user)}</strong> completed <Lex term="task" />
            <Badge color="success"  className="ms-2">
              {activity.task.name}
            </Badge>
          </div>
        )
      case 'task_created':
        return (
          <div>
            <strong>{getUserName(activity.user)}</strong> created <Lex term="task" />
            <Badge color="info"  className="ms-2">
              {activity.task.name}
            </Badge>
          </div>
        )
      case 'goal_created':
        return (
          <div>
            <strong>{getUserName(activity.user)}</strong> created goal
            <Badge color="warning"  className="ms-2">
              {activity.goal.name}
            </Badge>
          </div>
        )
      case 'strategy_created':
        return (
          <div>
            <strong>{getUserName(activity.user)}</strong> created strategy
            <Badge color="primary"  className="ms-2">
              {activity.strategy.name}
            </Badge>
          </div>
        )
      default:
        return `${getUserName(activity.user)} performed an action`
    }
  }


  return (
    <div 
      className={`p-3 border-0 border-bottom ${isNew ? 'activity-item-new' : ''} ${isUnseen ? 'activity-item-unseen' : ''}`}
      style={{
        ...(isNew ? {
          backgroundColor: '#f8f9fa',
          transition: 'background-color 0.3s ease-out'
        } : {}),
        ...(isUnseen ? {
          cursor: 'pointer'
        } : {})
      }}
      onClick={isUnseen ? (e) => {
        // Only mark as seen if clicking on the item itself, not on buttons or comment icons
        if (e.target.closest('button') || e.target.closest('.icon-button') || e.target.closest('.comment-trigger') || e.target.closest('.comments-toggle') || e.target.classList.contains('fas')) return
        onMarkAsSeen && onMarkAsSeen(activity.id)
      } : undefined}
      title={isUnseen ? 'Click to mark as seen' : undefined}
    >
      <div 
        ref={onVisibilityRef}
        className="d-flex align-items-start w-100"
        data-activity-id={activity.id}
      >
        {/* Unseen item indicator - subtle light gray vertical bar */}
        {isUnseen && (
          <div 
            className="activity-unseen-indicator"
            style={{
              width: '4px',
              backgroundColor: '#dee2e6',
              borderRadius: '2px',
              alignSelf: 'stretch',
              marginRight: '12px'
            }}
          />
        )}
        {/* New item indicator - blue vertical bar (less prominent than unseen) */}
        {isNew && !isUnseen && (
          <div 
            className="activity-new-indicator animate__animated animate__fadeIn"
            style={{
              width: '4px',
              backgroundColor: '#007bff',
              borderRadius: '2px',
              alignSelf: 'stretch',
              marginRight: '12px',
              animationDuration: '0.5s'
            }}
          />
        )}
      
        <div className="me-3 mt-1">
          {getActivityIcon()}
        </div>
        <div className="flex-grow-1">
          {getActivityDescription()}
          {/* Don't show date for journal entries - they have it in their content */}
          {activity.type !== 'journal_entry' && (
            <div className="mt-1">
              <small className="text-muted">
                <i className="fas fa-clock me-1"></i>
                <TimeAgo date={activity.createdAt} />
                {isUnseen && (
                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                    <i className="fas fa-eye-slash me-1"></i>UNSEEN
                  </span>
                )}
                {isNew && !isUnseen && (
                  <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem' }}>
                    NEW
                  </span>
                )}
              </small>
            </div>
          )}
        </div>
      
        {/* Action buttons area - only show for unseen items */}
        {isUnseen && onMarkAsSeen && (
          <div className="ms-2 d-flex flex-column align-items-center">
            <Button
              color="outline-secondary"
              size="sm"
              className="mb-1"
              style={{ 
                fontSize: '0.7rem', 
                padding: '4px 8px',
                borderRadius: '12px',
                opacity: 0.6,
                border: 'none'
              }}
              onClick={(e) => {
                e.stopPropagation()
                onMarkAsSeen(activity.id)
              }}
              title="Mark as seen"
            >
              <i className="fas fa-eye" style={{ fontSize: '0.8rem' }}></i>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if activity data, currentUser ID, or UI state changes
  return (
    prevProps.activity.id === nextProps.activity.id &&
    prevProps.activity.content === nextProps.activity.content &&
    prevProps.activity.type === nextProps.activity.type &&
    prevProps.currentUser?.id === nextProps.currentUser?.id &&
    prevProps.isNew === nextProps.isNew &&
    prevProps.isUnseen === nextProps.isUnseen
  )
})

const ActivityFeedPanel = React.forwardRef(({ userStore }, ref) => {
  const [activities, setActivities] = useState([])
  const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState(new Set())
  const [selectedTeams, setSelectedTeams] = useState(new Set())
  const [selectedPriorities, setSelectedPriorities] = useState(new Set())
  const [selectedStrategies, setSelectedStrategies] = useState(new Set())
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false)
  const [strategyDropdownOpen, setStrategyDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [newItemIds, setNewItemIds] = useState(new Set())
  
  // Use ref to track if component is mounted
  const isMountedRef = React.useRef(true)
  
  // Create ref for loadActivities to avoid initialization order issues
  const loadActivitiesRef = React.useRef(null)

  // Get team members and teams for team/business accounts - with safe defaults
  // The user data is directly in accountsUsers, not nested in a 'user' property
  const teamUsers = userStore?.account?.accountsUsers?.map(au => ({
    id: au.user_id || au.userId,
    firstName: au.first_name || au.firstName || au.user?.firstName || '',
    lastName: au.last_name || au.lastName || au.user?.lastName || '',
    email: au.email || au.user?.email || ''
  })) || []
  const teams = userStore?.account?.teams || []
  const isTeamAccount = userStore?.account?.accountType === 'team'
  const isBusinessAccount = userStore?.account?.accountType === 'business'
  const currentUser = userStore?.user
  
  // Count unique users in this account via AccountsUsers
  const accountsUsers = userStore?.account?.accountsUsers || []
  const uniqueUserIds = new Set(accountsUsers.map(au => au.user_id || au.userId).filter(id => id))
  const totalUsersInAccount = uniqueUserIds.size

  const loadActivities = useCallback(async () => {
    if (!userStore.account) {
      setActivities([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    // Clear new item indicators on full reload
    setNewItemIds(new Set())
    
    try {
      // Collect user IDs from direct selections and team selections
      let userIds = Array.from(selectedUsers)
      
      // Add users from selected teams for Business accounts
      if (isBusinessAccount && selectedTeams.size > 0) {
        // For now, if any team is selected, include all team members
        // TODO: Implement proper team-user relationships in the API
        const teamUserIds = teamUsers.map(user => user.id)
        userIds = [...new Set([...userIds, ...teamUserIds])]
      }

      // Build filter conditions for API
      const conditions = {}
      
      // Only add user filtering if users are specifically selected and not showing all
      if (selectedUsers.size > 0 && selectedUsers.size < totalUsersInAccount) {
        // Convert user IDs to accounts_user IDs
        const accountsUserIds = userIds.map(userId => {
          // If it's the current user, find their accounts_user record
          if (userId === currentUser.id) {
            return userStore.currentAccountsUser?.id
          }
          // For other users, find their accounts_user record in this account
          const accountsUser = userStore.account.accountsUsers?.find(au => au.user_id === userId)
          return accountsUser?.id
        }).filter(id => id) // Remove undefined values
        
        if (accountsUserIds.length > 0) {
          conditions.accounts_user_ids = accountsUserIds
        }
      }
      
      // Add priority filtering
      if (selectedPriorities.size > 0) {
        conditions.priority_ids = Array.from(selectedPriorities)
      }
      
      // Add strategy filtering  
      if (selectedStrategies.size > 0) {
        conditions.strategy_ids = Array.from(selectedStrategies)
      }
      
      // Load journal entries using UserStore method
      userStore.getJournalEntries(null, conditions, (data) => {
        // Don't update state if component is unmounted
        if (!isMountedRef.current) return
        
        
        const allActivities = []
        
        if (data.journalEntries) {
          const journalActivities = data.journalEntries
            .map(entry => {
              // Find the user from accountsUsers
              const accountsUser = userStore.account.accountsUsers?.find(au => au.id === entry.accountsUserId)
              const entryUser = accountsUser?.user || currentUser
              
              // Find related goal and task
              const goal = entry.goalId ? userStore.account.goals?.find(g => g.id === entry.goalId) : null
              const task = entry.taskId ? userStore.account.tasks?.find(t => t.id === entry.taskId) : null
              
              return {
                id: `journal-${entry.id}`,
                type: 'journal_entry',
                content: entry.content,
                user: entryUser,
                goal: goal,
                task: task,
                createdAt: new Date(entry.createdAt),
                journalEntry: entry // Keep original entry for CommentThread
              }
            })
          allActivities.push(...journalActivities)
        }
        
        // Load other activity types synchronously
        loadOtherActivitiesAndFinish(allActivities, userIds)
      })
    } catch (error) {
      console.error('Error loading activities:', error)
      if (isMountedRef.current) {
        setActivities([])
        setIsLoading(false)
      }
    }
  }, [userStore, selectedUsers, selectedTeams, selectedPriorities, selectedStrategies, currentUser])

  // Incremental update function - only fetches new activities
  const loadIncrementalActivities = useCallback(async () => {
    if (!userStore.account || !lastUpdated) {
      // If no lastUpdated timestamp, do a full reload
      return loadActivities()
    }

    try {
      // Build the same filter conditions as the full load
      // Note: Backend may not support 'since' parameter yet, so we'll filter client-side
      const conditions = {}
      
      // Add user filtering if needed
      if (selectedUsers.size > 0 && selectedUsers.size < totalUsersInAccount) {
        let userIds = Array.from(selectedUsers)
        if (isBusinessAccount && selectedTeams.size > 0) {
          const teamUserIds = teamUsers.map(user => user.id)
          userIds = [...new Set([...userIds, ...teamUserIds])]
        }
        
        const accountsUserIds = userIds.map(userId => {
          if (userId === currentUser.id) {
            return userStore.currentAccountsUser?.id
          }
          const accountsUser = userStore.account.accountsUsers?.find(au => au.user_id === userId)
          return accountsUser?.id
        }).filter(id => id)
        
        if (accountsUserIds.length > 0) {
          conditions.accounts_user_ids = accountsUserIds
        }
      }
      
      // Add priority/strategy filtering
      if (selectedPriorities.size > 0) {
        conditions.priority_ids = Array.from(selectedPriorities)
      }
      if (selectedStrategies.size > 0) {
        conditions.strategy_ids = Array.from(selectedStrategies)
      }

      // Load only new journal entries
      userStore.getJournalEntries(null, conditions, (data) => {
        if (!isMountedRef.current) return

        const newActivities = []
        
        if (data.journalEntries && data.journalEntries.length > 0) {
          // Filter entries that are newer than lastUpdated (client-side filtering)
          const newEntries = data.journalEntries.filter(entry => 
            new Date(entry.createdAt) > lastUpdated
          )

          const journalActivities = newEntries.map(entry => {
            const accountsUser = userStore.account.accountsUsers?.find(au => au.id === entry.accountsUserId)
            const entryUser = accountsUser?.user || currentUser
            const goal = entry.goalId ? userStore.account.goals?.find(g => g.id === entry.goalId) : null
            const task = entry.taskId ? userStore.account.tasks?.find(t => t.id === entry.taskId) : null
            
            return {
              id: `journal-${entry.id}`,
              type: 'journal_entry',
              content: entry.content,
              user: entryUser,
              goal: goal,
              task: task,
              createdAt: new Date(entry.createdAt),
              isNew: true, // Mark as new for visual indicator
              journalEntry: entry // Keep original entry for CommentThread
            }
          })
          
          newActivities.push(...journalActivities)
        }

        // Add new activities to the beginning of the list (avoiding full re-render)
        if (newActivities.length > 0) {
          setActivities(prevActivities => {
            // Merge new activities, removing duplicates and sorting
            const existingIds = new Set(prevActivities.map(a => a.id))
            const uniqueNewActivities = newActivities.filter(a => !existingIds.has(a.id))
            
            if (uniqueNewActivities.length === 0) return prevActivities
            
            const mergedActivities = [...uniqueNewActivities, ...prevActivities]
            mergedActivities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

            return mergedActivities
          })
          
          // Clear previous new indicators and track new item IDs for visual highlighting
          const newIds = newActivities.map(a => a.id)
          setNewItemIds(new Set(newIds)) // Replace with only new items
        } else {
          // If no new activities, clear all new indicators on this refresh
          setNewItemIds(new Set())
        }
        
        // Update timestamp
        setLastUpdated(new Date())
      })
    } catch (error) {
      console.error('Error loading incremental activities:', error)
      // Fallback to full reload on error
      loadActivities()
    }
  }, [userStore, selectedUsers, selectedTeams, selectedPriorities, selectedStrategies, currentUser, lastUpdated, loadActivities, totalUsersInAccount, isBusinessAccount, teamUsers])

  // Store loadActivities in ref for auto-update hook
  React.useEffect(() => {
    loadActivitiesRef.current = lastUpdated ? loadIncrementalActivities : loadActivities
  }, [loadActivities, loadIncrementalActivities, lastUpdated])

  // Auto-update mechanism — subscribe to realtime activity events via whereiswaldo (#629).
  useWaldoEvent(
    ['journal_entry_created', 'task_created', 'task_completed', 'goal_created', 'strategy_created', 'comment_created', 'mention_created'],
    () => {
      if (loadActivitiesRef.current) {
        loadActivitiesRef.current()
      }
    }
  )

  // Realtime connection status drives the indicator below; the polling fallback was
  // retired with Pusher (whereiswaldo's ActionCable transport replaces it).
  const isPollingEnabled = false
  const { connected: isGlobalPusherEnabled } = usePresenceContext() || {}

  // ViewRecord API for marking as seen
  const { markAsSeen, markMultipleAsSeen } = useViewRecords(userStore)

  // Activity visibility tracking
  const { observeActivity, unobserveActivity } = useActivityVisibility(markAsSeen, {
    threshold: 0.5, // 50% of item must be visible
    viewDuration: 2000 // Mark as seen after 2 seconds
  })

  // Helper functions for seen status
  const getUnseenCount = useCallback((activities) => {
    if (!activities || activities.length === 0) return 0
    return activities.filter(activity => activity.not_seen).length
  }, [])

  // Create ref callback for activity items
  const createVisibilityRef = useCallback((activityId) => {
    return (element) => {
      if (element) {
        observeActivity(element, activityId)
      } else {
        unobserveActivity(activityId)
      }
    }
  }, [observeActivity, unobserveActivity])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only handle shortcuts when not typing in inputs
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return
      
      // Ctrl/Cmd + Shift + A: Mark all as seen
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'A') {
        event.preventDefault()
        const unseenIds = activities.filter(a => a.not_seen).map(a => a.id)
        if (unseenIds.length > 0) {
          markMultipleAsSeen(unseenIds)
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [activities, markMultipleAsSeen])

  const loadOtherActivitiesAndFinish = (activities, userIds) => {
    try {
      // Load task-related activities (simplified - no filtering since tasks don't have user relationships yet)
      if (userStore.account.tasks && userIds.includes(currentUser.id)) {
        const taskActivities = userStore.account.tasks
          .map(task => {
            // Check if task was recently created (within last 30 days)
            const taskCreatedAt = new Date(task.createdAt)
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            
            if (taskCreatedAt < thirtyDaysAgo) return null
            
            return {
              id: `task-${task.id}`,
              type: 'task_created',
              user: currentUser,
              task: task,
              goal: task.goalId ? userStore.account.goals?.find(g => g.id === task.goalId) : null,
              createdAt: taskCreatedAt
            }
          })
          .filter(activity => activity !== null)
        activities.push(...taskActivities)
      }

      // Load strategy creation activities (simplified - no filtering since strategies don't have user relationships yet)
      if (userStore.account.strategies && userIds.includes(currentUser.id)) {
        const strategyActivities = userStore.account.strategies
          .map(strategy => {
            // Check if strategy was recently created (within last 30 days)
            const strategyCreatedAt = new Date(strategy.createdAt)
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            
            if (strategyCreatedAt < thirtyDaysAgo) return null
            
            return {
              id: `strategy-${strategy.id}`,
              type: 'strategy_created',
              user: currentUser,
              strategy: strategy,
              createdAt: strategyCreatedAt
            }
          })
          .filter(activity => activity !== null)
        activities.push(...strategyActivities)
      }

      // Load goal creation activities 
      if (userStore.account.goals) {
        const goalActivities = userStore.account.goals
          .map(goal => {
            // Find the user who created this goal
            const creatorAccountsUser = userStore.account.accountsUsers.find(au => au.id === goal.accountsUserId)
            const goalUser = creatorAccountsUser?.user || currentUser
            
            // Only include if this user is selected
            if (!userIds.includes(goalUser.id)) return null
            
            return {
              id: `goal-${goal.id}`,
              type: 'goal_created',
              user: goalUser,
              goal: goal,
              createdAt: new Date(goal.createdAt)
            }
          })
          .filter(activity => activity !== null)
        activities.push(...goalActivities)
      }

      // Sort activities by creation date (newest first)
      activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setActivities(activities)
        setIsLoading(false)
        setLastUpdated(new Date()) // Set timestamp for incremental updates
      }
    } catch (error) {
      console.error('Error loading other activities:', error)
      if (isMountedRef.current) {
        setActivities([])
        setIsLoading(false)
      }
    }
  }

  const getSelectedUserCount = () => {
    const totalUsers = selectedUsers.size
    const totalTeams = selectedTeams.size
    
    if (totalUsers === 0 && totalTeams === 0) return 'All Users'
    
    // Check if all users are selected
    if (totalUsers === totalUsersInAccount && totalTeams === 0) return 'All Users'
    
    if (isBusinessAccount) {
      if (totalUsers === 1 && totalTeams === 0 && currentUser && selectedUsers.has(currentUser.id)) return 'Just Me'
      
      const parts = []
      if (totalUsers > 0) parts.push(`${totalUsers} user${totalUsers !== 1 ? 's' : ''}`)
      if (totalTeams > 0) parts.push(`${totalTeams} team${totalTeams !== 1 ? 's' : ''}`)
      
      return parts.join(' + ')
    } else {
      if (totalUsers === 1 && currentUser && selectedUsers.has(currentUser.id)) return 'Just Me'
      if (totalUsers === 0) return 'All Users'
      return `${totalUsers} user${totalUsers !== 1 ? 's' : ''}`
    }
  }

  const getPriorityFilterText = () => {
    if (selectedPriorities.size === 0) return 'All Priorities'
    if (selectedPriorities.size === 1) {
      const priority = userStore.account.priorities?.find(p => selectedPriorities.has(p.id))
      return priority?.name || 'Priority'
    }
    return `${selectedPriorities.size} priorities`
  }

  const getStrategyFilterText = () => {
    if (selectedStrategies.size === 0) return 'All Strategies'
    if (selectedStrategies.size === 1) {
      const strategy = userStore.account.strategies?.find(s => selectedStrategies.has(s.id))
      return strategy?.name || 'Strategy'
    }
    return `${selectedStrategies.size} strategies`
  }

  const togglePriorityFilter = (priorityId) => {
    const newSelected = new Set(selectedPriorities)
    if (newSelected.has(priorityId)) {
      newSelected.delete(priorityId)
    } else {
      newSelected.add(priorityId)
    }
    setSelectedPriorities(newSelected)
  }

  const toggleStrategyFilter = (strategyId) => {
    const newSelected = new Set(selectedStrategies)
    if (newSelected.has(strategyId)) {
      newSelected.delete(strategyId)
    } else {
      newSelected.add(strategyId)
    }
    setSelectedStrategies(newSelected)
  }

  // Removed automatic current user selection - show all users by default

  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  // Expose loadActivities method via ref
  React.useImperativeHandle(ref, () => ({
    reloadActivities: loadActivities
  }), [loadActivities])

  // Cleanup effect to prevent memory leaks
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return (
    <div className="cbs-scope" style={{ margin: '8px' }}>
      <Card>
        <CardHeader className="bg-light">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fas fa-stream text-success me-2"></i>
              <h6 className="mb-0 fw-bold">Activity Feed</h6>
            
              {/* Auto-update status indicator */}
              {isGlobalPusherEnabled && (
                <Badge color="success" className="ms-2" id="pusher-status">
                  <i className="fas fa-bolt me-1"></i>Live
                </Badge>
              )}
              {isPollingEnabled && !isGlobalPusherEnabled && (
                <Badge color="info" className="ms-2" id="polling-status">
                  <i className="fas fa-clock me-1"></i>Auto
                </Badge>
              )}
            
              <Button
                id="reload-activity-button"
                color="outline-secondary"
                className="btn-sm ms-2"
                onClick={loadActivities}
                disabled={isLoading}
              >
                <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-redo-alt'}`}></i>
              </Button>
            
              {/* Mark all as seen button - only show if there are unseen items */}
              {getUnseenCount(activities) > 0 && (
                <Button
                  id="mark-all-seen-button"
                  color="outline-success"
                  className="btn-sm ms-1"
                  onClick={() => {
                    const unseenIds = activities.filter(a => a.not_seen).map(a => a.id)
                    if (unseenIds.length > 0) {
                      markMultipleAsSeen(unseenIds)
                    }
                  }}
                >
                  <i className="fas fa-eye me-1"></i>
                  Mark All Seen
                </Button>
              )}
            
              {/* Tooltips */}
              <Tooltip target="reload-activity-button" placement="bottom">
                Reload activity feed
              </Tooltip>
              {getUnseenCount(activities) > 0 && (
                <Tooltip target="mark-all-seen-button" placement="bottom">
                  Mark all activities as seen
                </Tooltip>
              )}
              {getUnseenCount(activities) > 0 && (
                <Tooltip target="keyboard-shortcuts-info" placement="bottom">
                  Keyboard shortcuts: Ctrl+Shift+A (Mark all seen)
                </Tooltip>
              )}
              {isGlobalPusherEnabled && (
                <Tooltip target="pusher-status" placement="bottom">
                  Real-time updates connected
                </Tooltip>
              )}
              {isPollingEnabled && (
                <Tooltip target="polling-status" placement="bottom">
                  Auto-refresh every minute
                </Tooltip>
              )}
            </div>
            <div className="d-flex align-items-center gap-1 flex-wrap">
              {/* Priority Filter */}
              {userStore.account?.priorities?.length > 0 && (
                <Dropdown 
                  isOpen={priorityDropdownOpen} 
                  toggle={() => setPriorityDropdownOpen(!priorityDropdownOpen)}
                >
                  <DropdownToggle caret color="outline-info" className="btn-sm">
                    <i className="fas fa-bullseye me-1"></i>
                    {getPriorityFilterText()}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => setSelectedPriorities(new Set())}>
                      <i className="fas fa-times me-1"></i>
                      Clear All
                    </DropdownItem>
                    <DropdownItem divider />
                    {userStore.account.priorities.map(priority => (
                      <DropdownItem 
                        key={priority.id}
                        onClick={() => togglePriorityFilter(priority.id)}
                      >
                        <span className={`fas ${selectedPriorities.has(priority.id) ? 'fa-check-square' : 'fa-square'} me-1`}></span>
                        {priority.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}

              {/* Strategy Filter */}
              {userStore.account?.strategies?.length > 0 && (
                <Dropdown 
                  isOpen={strategyDropdownOpen} 
                  toggle={() => setStrategyDropdownOpen(!strategyDropdownOpen)}
                >
                  <DropdownToggle caret color="outline-warning" className="btn-sm">
                    <i className="fas fa-chess me-1"></i>
                    {getStrategyFilterText()}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => setSelectedStrategies(new Set())}>
                      <i className="fas fa-times me-1"></i>
                      Clear All
                    </DropdownItem>
                    <DropdownItem divider />
                    {userStore.account.strategies.map(strategy => (
                      <DropdownItem 
                        key={strategy.id}
                        onClick={() => toggleStrategyFilter(strategy.id)}
                      >
                        <span className={`fas ${selectedStrategies.has(strategy.id) ? 'fa-check-square' : 'fa-square'} me-1`}></span>
                        {strategy.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}

              {/* User Filter for Team and Business Accounts - only show if multiple users */}
              {(isTeamAccount || isBusinessAccount) && totalUsersInAccount > 1 && (
                <Button
                  color="outline-primary"
                  className="btn-sm d-flex align-items-center"
                  onClick={() => setIsUserSelectorOpen(true)}
                >
                  <i className="fas fa-users me-1"></i>
                  {getSelectedUserCount()}
                </Button>
              )}
            
              <Badge color="secondary">{activities.length}</Badge>
              {getUnseenCount(activities) > 0 && (
                <Badge color="secondary" className="ms-1" style={{ opacity: 0.8 }}>
                  <i className="fas fa-eye-slash me-1"></i>
                  {getUnseenCount(activities)} unseen
                </Badge>
              )}
            
              {/* Keyboard shortcuts info */}
              {getUnseenCount(activities) > 0 && (
                <Button
                  id="keyboard-shortcuts-info"
                  color="link"
                  className="btn-sm p-1 ms-1"
                  style={{ fontSize: '0.7rem', color: '#6c757d' }}
                >
                  <i className="fas fa-keyboard"></i>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      
        <CardBody className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {isLoading ? (
            <div className="text-center p-4">
              <i className="fas fa-spinner fa-spin me-2"></i>
              Loading activities...
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <i className="fas fa-stream fa-2x mb-2 d-block"></i>
              No recent activity
            </div>
          ) : (
            <div>
              {activities.map((activity, index) => (
                <ActivityItem 
                  key={`${activity.id}-${activity.type}-${index}`} 
                  activity={activity} 
                  currentUser={currentUser}
                  isNew={newItemIds.has(activity.id)}
                  isUnseen={activity.not_seen}
                  onVisibilityRef={createVisibilityRef(activity.id)}
                  onMarkAsSeen={markAsSeen}
                />
              ))}
            </div>
          )}
        </CardBody>

        {/* User Selector Modal for Team and Business Accounts - only show if multiple users */}
        {(isTeamAccount || isBusinessAccount) && totalUsersInAccount > 1 && (
          <UserSelectorModal
            isOpen={isUserSelectorOpen}
            toggle={() => setIsUserSelectorOpen(false)}
            users={teamUsers}
            teams={teams}
            currentUser={currentUser}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            selectedTeams={selectedTeams}
            setSelectedTeams={setSelectedTeams}
            accountType={userStore.account?.accountType}
          />
        )}
      </Card>
    </div>
  )
})

export default withUserStore(ActivityFeedPanel)
